import * as React from 'react';
import * as _ from 'lodash';
import { Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  ContentLoading,
  ControlState,
  ControlStyleClass,
  ControlValidation,
  DomainVerificationStatus,
  Logging,
  renderEdit,
  Notification,
  ResponseError,
  API,
  NotificationData,
  DomainVerification,
  getUuidV4,
  ConfirmationModal,
  getCreatePermission,
  PermissionGroup,
  UserContext,
  UserInfo,
  getEditPermission,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import ConfigurationStatusProps from './ConfigurationStatus.interface';
import useConfiguration, { DefaultDomainName } from '../useConfiguration';
import '../../common-correspondence.scss';
import '../configuration.scss';

const styles: ControlStyleClass = null;

const state: ControlState = {
  editable: false,
  disabled: true,
  readOnly: false,
};

const validation: ControlValidation = {
  requiredFields: ['fromEmailAddress', 'errorEmailAddress'],
  customMessages: [],
};

const DomainVerificationRefreshIntervalMinutes = 1;

const ConfigurationStatus = ({
  gatewayEndPoint,
}: ConfigurationStatusProps): JSX.Element => {
  const [reloadKey, setReloadKey] = React.useState(getUuidV4());
  const [configuration, configurationNotification] = useConfiguration(gatewayEndPoint, reloadKey);
  const [redirect, setRedirect] = React.useState(null);
  const [notification, setNotification] = React.useState(null);
  const [domainVerificationStatus, setDomainVerificationStatus] = React.useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = React.useState(false);

  const dispatch = useDispatch();

  const getDomainVerification = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      const verification: DomainVerification = await api.correspondence.getDomainVerificationV1(configuration?.domain);

      Logging.Info('Get Domain Verification:', verification);

      // Check if the Status belongs to the Requested Domain
      if (verification?.domain === configuration?.domain) {
        setDomainVerificationStatus(verification?.status);
      } else {
        setDomainVerificationStatus(null);
      }
    } catch (error) {
      const domainVerificationNotification: NotificationData = {
        type: 'Error',
        title: 'Domain Verification Read Error',
        description: 'Unable to read Domain Verification',
        error: error as ResponseError,
      };
      setNotification(domainVerificationNotification);
    }
  };

  const resetConfiguration = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      await api.correspondence.deleteEmailConfigurationV41();

      // Force a Reload
      setReloadKey(getUuidV4());
    } catch (error) {
      const configurationResetNotification: NotificationData = {
        type: 'Error',
        title: 'Configuration Reset Error',
        description: 'Unable to reset Configuration',
        error: error as ResponseError,
      };
      setNotification(configurationResetNotification);
    }
  };

  const getDomainVerificationHeading = (status: DomainVerificationStatus): string => {
    if (status) {
      switch (status) {
        case DomainVerificationStatus.Success:
          return 'Configuration Complete';
        case DomainVerificationStatus.Failed:
        case DomainVerificationStatus.TemporaryFailure:
          return 'Configuration Failed';
        case DomainVerificationStatus.Pending:
          return 'Configuration Incomplete';
        case DomainVerificationStatus.NotStarted:
          return 'Configuration Incomplete';
        default:
          return null;
      }
    }

    return 'Configuration Unknown';
  };

  const getDomainVerificationDescription = (status: DomainVerificationStatus): string => {
    if (status) {
      switch (status) {
        case DomainVerificationStatus.Success:
          // eslint-disable-next-line max-len
          return 'The configuration is complete, correspondence will be sent from the domain below. To change the domain click \'Reconfigure Domain\'.';
        case DomainVerificationStatus.Failed:
        case DomainVerificationStatus.TemporaryFailure:
          return 'The configuration has failed. To change the domain click \'Reconfigure Domain\'.';
        case DomainVerificationStatus.Pending:
          // eslint-disable-next-line max-len
          return 'Once the configuration is complete, correspondence will be sent from the domain below. To change the domain click \'Reconfigure Domain\'.';
        case DomainVerificationStatus.NotStarted:
          // eslint-disable-next-line max-len
          return 'Once the configuration is complete, correspondence will be sent from the domain below. To change the domain click \'Reconfigure Domain\'.';
        default:
          return null;
      }
    }

    return 'Please wait until the status becomes available.';
  };

  const getDomainVerificationStatusIcon = (status: DomainVerificationStatus): JSX.Element => {
    if (status) {
      switch (status) {
        case DomainVerificationStatus.Success:
          return (<VtxIcon type="check-circle" color="green" fontSize={20} />);
        case DomainVerificationStatus.Failed:
        case DomainVerificationStatus.TemporaryFailure:
          return (<VtxIcon type="close-circle" color="red" fontSize={20} />);
        case DomainVerificationStatus.Pending:
          return (<img src="images/loading-animation.svg" className="verificationImage" alt="Unverified" />);
        case DomainVerificationStatus.NotStarted:
          return (<VtxIcon type="hourglass" color="gray" fontSize={20} />);
        default:
          return null;
      }
    }

    // Unknown Status
    return (<VtxIcon type="question" color="blue" fontSize={20} />);
  };

  // Reset the Configuration
  const onResetConfiguration = (): void => {
    setIsConfirmationOpen(true);
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    setNotification(null);
  };

  const onOKConfirmation = (): void => {
    Logging.Info('OK Confirmation');
    setIsConfirmationOpen(false);
    dispatch(resetConfiguration());
  };

  const onCancelConfirmation = (): void => {
    Logging.Info('Cancel Confirmation');
    setIsConfirmationOpen(false);
  };

  React.useEffect(() => {
    let interval: NodeJS.Timeout = null;

    // Start a Timer to read the Status can still Change
    if ((domainVerificationStatus === DomainVerificationStatus.NotStarted)
      || (domainVerificationStatus === DomainVerificationStatus.Pending)
      || (domainVerificationStatus === DomainVerificationStatus.TemporaryFailure)) {
      // Interval Load of Domain Verifications
      interval = setInterval(
        dispatch(getDomainVerification),
        DomainVerificationRefreshIntervalMinutes * 60 * 1000,
      );
    }

    return (): void => {
      clearInterval(interval);
    };
  }, [domainVerificationStatus]);

  // Load the Initial Domain Verification
  React.useEffect(() => {
    if (configuration) {
      // Domain Already configured
      if (configuration.domain) {
        dispatch(getDomainVerification());
      } else {
        // Direct to Configuration Steps if no Domain is configured
        setRedirect('/correspondence/configuration/steps');
      }
    }
  }, [configuration]);

  React.useEffect(() => {
    setNotification(configurationNotification);
  }, [configurationNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Configuration: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setNotification(null);
      setRedirect(null);
      setIsConfirmationOpen(false);
      Logging.Info('Configuration Unmounting');
    };
  }, []);

  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  const Spacer = () => <span style={{ marginRight: '10px' }} />;

  const status: DomainVerificationStatus = domainVerificationStatus;
  const vertexDomain: boolean = configuration?.domain?.toLowerCase() === DefaultDomainName.toLowerCase();

  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);

  // Get the Correspondence - Configuration Create & Edit Permission
  const hasConfigurationCreatePermission: boolean = getCreatePermission(userContext, PermissionGroup.CorrespondenceConfiguration);
  const hasConfigurationEditPermission: boolean = getEditPermission(userContext, PermissionGroup.CorrespondenceConfiguration);

  return (
    <>
      <div className="correspondenceContainer">
        <div className="correspondenceHeader">
          <div className="correspondenceHeaderLeft">
            Configuration
          </div>
          {
            configuration && hasConfigurationEditPermission && !vertexDomain
            && (
              <div className="correspondenceHeaderRight">
                <a href="#/correspondence/configuration/steps" title="Reconfigure Domain">
                  <VtxButton
                    id="reconfigureButton"
                    type="primary"
                  >
                    Reconfigure Domain
                  </VtxButton>
                </a>
                <Spacer />
                <VtxButton
                  id="resetButton"
                  type="default"
                  onClick={onResetConfiguration}
                >
                  Reset Configuration
                </VtxButton>
              </div>
            )
          }
        </div>
        {
          configuration && vertexDomain
          && (
            <>
              <div className="correspondenceContent stepsContainer">
                <div className="stepContainer">
                  <div className="stepContent">
                    {
                      hasConfigurationCreatePermission
                        ? (
                          <>
                            <div className="descriptionHeading">
                              {/* eslint-disable-next-line max-len */}
                              {'You are currently using Vertex\'s default domain for all correspondence actions. To configure your own domain, click "Configure Domain" and follow the proper verification steps.'}
                            </div>
                            <br />
                            <br />
                            <div className="centerText">
                              <a href="#/correspondence/configuration/steps" title="Reconfigure Domain">
                                <VtxButton
                                  id="reconfigureButton"
                                  type="primary"
                                >
                                  Configure Domain
                                </VtxButton>
                              </a>
                            </div>
                          </>
                        )
                        : (
                          <>
                            <div className="descriptionHeading">
                              {/* eslint-disable-next-line max-len */}
                              {'You are currently using Vertex\'s default domain for all correspondence actions. You do not have permission to configure your own domain.'}
                            </div>
                          </>
                        )
                    }
                  </div>
                </div>
              </div>
            </>
          )
        }
        {
          configuration && !vertexDomain
          && (
            <>
              <div className="correspondenceContent stepsContainer">
                <div className="stepContainer">
                  <div className="stepContent">
                    <div className="descriptionHeading">
                      {getDomainVerificationHeading(status)}
                    </div>
                    <div className="descriptionText">
                      {getDomainVerificationDescription(status)}
                    </div>
                    <br />
                    <br />
                    <div className="divTable">
                      <div className="divTableHeading">
                        <div className="divTableRow">
                          <div className="divTableHead statusCell">
                            Status
                          </div>
                          <div className="divTableHead">
                            Domain
                          </div>
                        </div>
                      </div>
                      <div className="divTableBody">
                        <div className="divTableRow">
                          <div className="divTableCell statusCell">
                            <div className="status">
                              {getDomainVerificationStatusIcon(status)}&nbsp;&nbsp;&nbsp;{status || 'Unknown'}
                            </div>
                          </div>
                          <div className="divTableCell">
                            <a href={`//${configuration?.domain}`} target="_blank" rel="noreferrer">
                              {configuration?.domain}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                    <br />
                    <br />
                    <br />
                    <div className="descriptionHeading">
                      Correspondence Addresses
                    </div>
                    <br />
                    <div className="addresses">
                      {renderEdit(100, 'fromEmailAddress', 'Send From Address', 'text', null,
                        configuration?.fromEmailAddress, null, state, styles, validation)}
                      {renderEdit(100, 'errorEmailAddress', 'Master Notification Address', 'text', null,
                        configuration?.errorEmailAddress, null, state, styles, validation)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        }
        {
          !configuration && !configurationNotification
          && (
            <div className="correspondenceContent">
              <ContentLoading loadingMessage="Loading Configuration ..." />
            </div>
          )
        }
      </div>
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        heading="Are you sure you want to reset configuration?"
        message="Resetting the domain configuartion will delete your current domain configuration and reset to the default Vertex Configuration."
        okButtonName="Continue"
        onOK={onOKConfirmation}
        cancelButtonName="Cancel"
        onCancel={onCancelConfirmation}
        confirmationType="Warning"
      />
    </>
  );
};

export default ConfigurationStatus;
